import React from "react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useRestrictCopyPaste } from "@/hooks/useRestrictCopyPaste";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface Question {
  id: string;
  question_text: string;
  question_type: string;
  options?: { [key: string]: string };
}

interface QuestionDisplayProps {
  question: Question;
  response: { answer?: string; selected_option?: string };
  onAnswerChange: (answer: string) => void;
  onOptionSelect: (option: string) => void;
}

export const QuestionDisplay = React.memo(
  ({
    question,
    response,
    onAnswerChange,
    onOptionSelect,
  }: QuestionDisplayProps) => {
    useRestrictCopyPaste({ window, actions: ["copy", "cut", "paste"] });

    const isMCQ = question.question_type === "mcq";

    // Parse options - handle different possible formats
    let options: { key: string; value: string }[] = [];
    if (question.options && typeof question.options === "object") {
      options = Object.entries(question.options).map(([key, value]) => ({
        key,
        value,
      }));
    }

    return (
      <div className="space-y-4">
        <div className="text-lg font-medium">
          <div className="prose dark:prose-invert max-w-none question-markdown-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {question.question_text}
            </ReactMarkdown>
          </div>
        </div>

        {isMCQ ? (
          <RadioGroup
            value={response.selected_option || ""}
            onValueChange={onOptionSelect}
          >
            <div className="grid gap-3">
              {options.length > 0 ? (
                options.map((option: { key: string; value: string }) => (
                  <Label
                    key={option.key}
                    htmlFor={`question-${question.id}-option-${option.key}`}
                    className="flex items-start space-x-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                  >
                    <RadioGroupItem
                      value={option.key}
                      id={`question-${question.id}-option-${option.key}`}
                      className="shrink-0 mt-1"
                    />
                    <div className="flex-1">
                      <span className="font-medium">{option.key}.</span>{" "}
                      <div className="prose prose-sm dark:prose-invert max-w-none inline question-markdown-body">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {option.value}
                        </ReactMarkdown>
                      </div>
                    </div>
                  </Label>
                ))
              ) : (
                <div className="text-gray-500 italic">
                  No options available for this question
                </div>
              )}
            </div>
          </RadioGroup>
        ) : (
          <Textarea
            id="answer"
            placeholder="Enter your answer here... (Optional)"
            value={response.answer || ""}
            onChange={(e) => onAnswerChange(e.target.value)}
            className="min-h-32"
          />
        )}
      </div>
    );
  }
);

QuestionDisplay.displayName = "QuestionDisplay";
